import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Autocomplete,
  Box,
  Grid,
  Typography,
  InputAdornment,
  CircularProgress,
  Alert,
  MenuItem,
} from "@mui/material";
import { UrlApi } from "../../../services/apirest";
import Notify from "../../Notify";
import LinkPagoGeneradoGranel from "./componentes/LinkPagoGeneradoGranel";
import {
  crearLinkGranel,
  anularLinkGranel,
  mensajeError,
} from "./services/linkGranelMp.service";

const REFERENCIA_MAX = 20;

// Cuotas (solo admin puede cambiarlas; si no, la API usa el default: 12, sin interés 2 a 6)
const CUOTAS_MAX_PERMITIDAS = 12;
const CUOTAS_DEFAULT = { max: 12, sinInteresHasta: 6 };
const SIN_INTERES_NINGUNA = "ninguna";
// Topes de cuotas sin interés que acepta Mercado Pago (otro valor da error)
const CUOTAS_SIN_INTERES_PERMITIDAS = [3, 6, 9, 12];

const GenerarLinkPagoGranel = ({ open, onClose, onGenerado }) => {
  const esAdmin = localStorage.getItem("role") === "admin";

  const [clientes, setClientes] = useState([]);
  const [cliente, setCliente] = useState(null);
  const [monto, setMonto] = useState("");
  const [referencia, setReferencia] = useState("");
  const [cuotasMax, setCuotasMax] = useState(CUOTAS_DEFAULT.max);
  const [sinInteresHasta, setSinInteresHasta] = useState(CUOTAS_DEFAULT.sinInteresHasta);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [paymentLink, setPaymentLink] = useState(null);
  const [linkCopied, setLinkCopied] = useState(false);

  const [notify, setNotify] = useState({
    open: false,
    mensaje: "",
    severity: "success",
    color: "#D4EFDF",
  });

  useEffect(() => {
    if (!open) return;
    // Reset al abrir
    setCliente(null);
    setMonto("");
    setReferencia("");
    setCuotasMax(CUOTAS_DEFAULT.max);
    setSinInteresHasta(CUOTAS_DEFAULT.sinInteresHasta);
    setError(null);
    setPaymentLink(null);
    setLinkCopied(false);

    fetch(UrlApi + "/clientes")
      .then((res) => res.json())
      .then((data) => setClientes(Array.isArray(data) ? data : []))
      .catch(() => setClientes([]));
  }, [open]);

  const montoNum = Number(monto);
  const formValido =
    cliente &&
    Number.isFinite(montoNum) &&
    Number(montoNum.toFixed(2)) > 0 &&
    referencia.trim() !== "" &&
    referencia.length <= REFERENCIA_MAX;

  const handleGenerar = async () => {
    if (!formValido) return;
    setLoading(true);
    setError(null);

    let idRegistro = null;
    const idEmpresa = cliente.id_empresa || null;
    const textoReferencia = referencia.trim();
    try {
      // 1. Crear el registro en la base (estado 'generado')
      const bodyRegistro = {
        id_cliente: cliente.id,
        monto: Number(montoNum.toFixed(2)),
        referencia: textoReferencia || null,
        id_empresa: idEmpresa,
        usuario: localStorage.getItem("user"),
        estado: "generado",
        activo: 1,
      };

      const respRegistro = await fetch(UrlApi + "/granel_link_pago/", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(bodyRegistro),
      });
      if (!respRegistro.ok) throw new Error("No se pudo registrar el link de pago");
      const dataRegistro = await respRegistro.json();
      idRegistro = dataRegistro.id;

      // 2. Generar el link en Mercado Pago. La API toma monto y referencia del
      // registro, arma el external_reference (glp_<id>) y guarda link y order_id
      // en el registro.
      // Solo un admin envía cuotas; para el resto la API aplica el default
      const cuotas = esAdmin
        ? {
            max: cuotasMax,
            sinInteresHasta:
              sinInteresHasta === SIN_INTERES_NINGUNA ? null : sinInteresHasta,
          }
        : undefined;
      const { checkout_url, order_id, external_reference } = await crearLinkGranel(
        idEmpresa,
        idRegistro,
        cuotas
      );

      setPaymentLink({
        url: checkout_url,
        external_reference,
        order_id,
        referencia: textoReferencia,
      });
      setNotify({
        open: true,
        mensaje: "Link de pago generado",
        severity: "success",
        color: "#D4EFDF",
      });
      onGenerado && onGenerado();
    } catch (err) {
      console.error("Error generando link de pago granel:", err);
      // Si el registro quedó creado, se anula para no dejarlo huérfano. La API
      // además cancela la order en MP si llegó a crearse; si la API no responde,
      // se anula directo en la base.
      if (idRegistro) {
        anularLinkGranel(idEmpresa, idRegistro).catch(() =>
          fetch(UrlApi + "/granel_link_pago/anular/" + idRegistro, {
            method: "PUT",
            headers: { "Content-Type": "application/json" },
          }).catch(() => {})
        );
      }
      setError(mensajeError(err, "Error generando el link de pago"));
    } finally {
      setLoading(false);
    }
  };

  const copiarLink = () => {
    if (!paymentLink?.url) return;
    navigator.clipboard
      .writeText(paymentLink.url)
      .then(() => {
        setLinkCopied(true);
        setTimeout(() => setLinkCopied(false), 2000);
      })
      .catch(() => setError("No se pudo copiar al portapapeles"));
  };

  return (
    <>
      {/* Mientras se genera el link no se permite cerrar, para no cortar el proceso a mitad */}
      <Dialog open={open} onClose={loading ? undefined : onClose} maxWidth="md" fullWidth>
        <Box backgroundColor="#1F618D">
          <DialogTitle>
            <Typography variant="h6" align="center" color="#ffffff">
              Generar link de pago
            </Typography>
          </DialogTitle>
        </Box>

        <DialogContent dividers>
          {loading ? (
            <Box
              display="flex"
              justifyContent="center"
              alignItems="center"
              minHeight={200}
            >
              <CircularProgress />
            </Box>
          ) : paymentLink ? (
            <LinkPagoGeneradoGranel
              paymentLink={paymentLink}
              linkCopied={linkCopied}
              onCopiarLink={copiarLink}
            />
          ) : (
            <Grid container spacing={2} mt={0}>
              <Grid item xs={12}>
                <Autocomplete
                  options={clientes}
                  value={cliente}
                  noOptionsText="Cliente inexistente"
                  getOptionLabel={(c) => (c?.razon_social ? `${c.razon_social} - ${c.id}` : "")}
                  isOptionEqualToValue={(opt, val) => opt.id === val.id}
                  onChange={(e, value) => setCliente(value)}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Cliente"
                      placeholder="Tipee para buscar cliente.."
                    />
                  )}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Monto"
                  type="number"
                  value={monto}
                  onChange={(e) => setMonto(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">$</InputAdornment>
                    ),
                  }}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Referencia"
                  value={referencia}
                  onChange={(e) =>
                    setReferencia(e.target.value.slice(0, REFERENCIA_MAX))
                  }
                  inputProps={{ maxLength: REFERENCIA_MAX }}
                  helperText={`${referencia.length}/${REFERENCIA_MAX}`}
                />
              </Grid>

              {esAdmin && (
                <>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      select
                      fullWidth
                      label="Cuotas máximas"
                      value={cuotasMax}
                      onChange={(e) => {
                        const max = Number(e.target.value);
                        setCuotasMax(max);
                        // El rango sin interés no puede superar el máximo de cuotas
                        // (se pasa al mayor tope permitido que entre, o a ninguna)
                        if (sinInteresHasta !== SIN_INTERES_NINGUNA && sinInteresHasta > max) {
                          const tope = CUOTAS_SIN_INTERES_PERMITIDAS.filter((n) => n <= max).pop();
                          setSinInteresHasta(tope || SIN_INTERES_NINGUNA);
                        }
                      }}
                    >
                      {Array.from({ length: CUOTAS_MAX_PERMITIDAS }, (_, i) => i + 1).map((n) => (
                        <MenuItem key={n} value={n}>
                          {n === 1 ? "1 (sin cuotas)" : n}
                        </MenuItem>
                      ))}
                    </TextField>
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <TextField
                      select
                      fullWidth
                      label="Sin interés"
                      value={sinInteresHasta}
                      onChange={(e) =>
                        setSinInteresHasta(
                          e.target.value === SIN_INTERES_NINGUNA
                            ? SIN_INTERES_NINGUNA
                            : Number(e.target.value)
                        )
                      }
                    >
                      <MenuItem value={SIN_INTERES_NINGUNA}>Ninguna (todas con interés)</MenuItem>
                      {CUOTAS_SIN_INTERES_PERMITIDAS.filter((n) => n <= cuotasMax).map((n) => (
                        <MenuItem key={n} value={n}>
                          De 2 a {n} cuotas
                        </MenuItem>
                      ))}
                    </TextField>
                  </Grid>
                </>
              )}

              {error && (
                <Grid item xs={12}>
                  <Alert severity="error">{error}</Alert>
                </Grid>
              )}
            </Grid>
          )}
        </DialogContent>

        <DialogActions>
          <Button onClick={onClose} color="error" variant="outlined" disabled={loading}>
            {paymentLink ? "Cerrar" : "Cancelar"}
          </Button>
          {!paymentLink && (
            <Button
              onClick={handleGenerar}
              color="success"
              variant="contained"
              disabled={!formValido || loading}
            >
              Generar link
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {notify.open && (
        <Notify
          mensaje={notify.mensaje}
          open={notify.open}
          color={notify.color}
          severity={notify.severity}
          handleClose={() => setNotify({ ...notify, open: false })}
        />
      )}
    </>
  );
};

export default GenerarLinkPagoGranel;
